import {dataset, classList, cssVars, listen, dispatch, lazyCustomElements} from 'nuffjs';
import {dialogsStack} from './dialogs-stack';
import {getScrollLeft, getScrollTop} from '../utils/get-scroll';
import {ucFirst} from '../utils/string';
import {threeStateTransition} from '../utils/animations';
import {onFrame, waitFrame} from '../utils/wait';

/**
 * configuration values
 *
 * @property {string} coveredClass=coveredByDialog class added to the root elements when a modal
 * dialog opens
 * @property {string} openedClass=opened class used to trigger the opening transition. It is applied
 * as a three state transition @see {@link ThreeStateTransition}
 * @property {string} fixedClass=fixed class to set an element position to fixed
 * @property {string} leftProp=left css custom property set on the root elements with their position
 * when the dialog opens
 * @property {string} topProp=top css custom property set on the root elements with their position
 * when the dialog opens
 * @property {string} rootElementsSelector used to select the other root elements. See code for the
 * default value
 * @property {string} contentAttr=content data attribute used to select the content element
 * @property {string} suspendibleAttr=suspendible data attribute used to find suspendible elements
 * in the document
 * @property {string} beforeOpenEvent=dialog:beforeOpen cancelable event dispatched before opening
 * @property {string} openEvent=dialog:open event dispached after opening
 * @property {string} beforeCloseEvent=dialog:beforeClose cancelable event dispatched before closing
 * @property {string} closeEvent=dialog:close event dispatched after closing
 * @memberof Dialog
 */
const conf = {
	coveredClass: 'coveredByDialog',
	openedClass: 'opened',
	fixedClass: 'fixed',
	leftProp: 'left',
	topProp: 'top',
	rootElementsSelector: ':scope > *:not(script):not(noscript):not(svg):not(link):not(style)',
	contentAttr: 'content',
	suspendibleAttr: 'suspendible',
	beforeOpenEvent: 'dialog:beforeOpen',
	openEvent: 'dialog:open',
	beforeCloseEvent: 'dialog:beforeClose',
	closeEvent: 'dialog:close',
};

/**
 * @classdesc Can be used for both modal and not modal dialogs.
 *
 * Modal dialogs (which are 99% of our cases, we use it for main menu on small screens, galleries,
 * and so on) must be on the root (first level of the body) and if they are not there, they will
 * move themself on the root before opening and restore their position when closed. Modal dialogs
 * also set a fixed class and left and top properties for all other elements in the root, in order
 * to be the only element contributing to the document height, with the body that will be scrollable
 * accordingly to the dialog size.
 * It uses an instance of {@link DialogsStack} to coordinate with other dialogs.
 *
 * @class Dialog
 * @extends {HTMLElement}
 * @version 1.0.2
 * @path
 *
 * @emits dialog:beforeOpen
 * @emits dialog:open
 * @emits dialog:beforeClose
 * @emits dialog:close
 *
 * @listens click
 * @listens keydown
 * @listens focus
 */
class Dialog extends HTMLElement {
	// we make the conf available to the code that imports the class
	static conf = conf;

	/**
	 * @property {?string} id the unique identifier of the dialog
	 * @property {boolean} [modal=false] set if the dialog is modal
	 * @property {boolean} [coverable=false] Indicates whether another dialog can open without
	 * closing this one
	 * @property {boolean} [closeOnEsc=true] allow the modal dialog to be closed by pressing the Esc
	 * key
	 * @property {boolean} [closeOnClick=true] allow to close the dialog when clicking outside or
	 * directly on the element itself (usually on a pseudoelement used as a backdrop, since the
	 * dialog have a content element nested inside)
	 * @property {boolean} [suspendElements=true] allow to suspend other components that declare
	 * themself as suspendible using the specific attribute @see {@link Dialog.conf}
	 * @memberof Dialog
	 */
	static defaultOpts = {
		id: null,
		modal: false,
		coverable: false,
		closeOnEsc: true,
		closeOnClick: true,
		suspendElements: true,
	};

	opened = false;
	promise = Promise.resolve();
	resolvePromise = null;
	originalParent = null;
	originalNextSibling = null;
	previousScrollPos = {left: 0, top: 0};
	rootElements = [];
	linkListener = null;
	clickListener = null;
	busy = false;
	previousActiveElement = null;
	eventListeners = [];
	suspendedElements = [];

	/**
	 * Initialization
	 */
	firstConnectedCallback() {
		/**
		 * options set by overriding the {@link Dialog.defaultOpts} with the data-attributes
		 * @type {object}
		 * @instance
		 */
		this.opts = dataset(this).get(this.constructor.defaultOpts);
		this.dialogId = 'id' in this.opts ? this.opts.id : null;
		if (!this.dialogId) {
			console.error('Dialogs must have a data-id', this);
		}
		this.coveredByClass = conf.coveredClass + ucFirst(this.dialogId);
	}

	/**
	 * Opens the dialog
	 *
	 * @param {object} [params={}] eventual parameters used by the dialog
	 * @param {(string|DocumentFragment)} [params.content] used to replace the dialog content
	 * @returns {Promise} resolve when the opening process is finished or immediately if it is not
	 * allowed to open
	 * @memberof Dialog
	 */
	async open(params = {}) {
		if (this.opened) {
			return this.promise;
		}
		// we give the opportunity to the outer world to block the process by canceling the event
		const opts = this.opts;
		const beforeEvent = dispatch(this, conf.beforeOpenEvent, {
			bubbles: true,
			cancelable: true,
		});
		if (beforeEvent.defaultPrevented) {
			return this.promise;
		}
		// if is modal and there is another one opened and is not coverable, we close it
		if (opts.modal) {
			const lastModal = dialogsStack.getLastModal();
			if (lastModal && !lastModal.isCoverable()) {
				this.promise = this.promise.then(() => lastModal.close());
			}
		}
		// we process the params before opening because usually they prepare the content
		this.processParams(params);
		this.opened = true;
		this.promise = this.promise
			.then(() => this.opening())
			.then(() => {
				// we save the element previously focused (usually the dialog toggler) because we
				// will try to restore the focus to it when the dialog closes
				this.previousActiveElement = document.activeElement;
				dialogsStack.push(this);
				this.removeAttribute('aria-hidden');
				this.focus({preventScroll: true});
				// we setup events to keep the focus inside the dialog
				this.addEvents();
				// we suspend elements that support the SuspendibleInterface
				if (opts.suspendElements) {
					this.suspendElementsOutside();
				}
				dispatch(this, conf.openEvent, {
					bubbles: true,
					cancelable: false,
				});
			});
		return this.promise;
	}

	/**
	 * Closes the dialog
	 *
	 * @returns {Promise} resolve when the closing process is finished or immediately if it is not
	 * allowed to close
	 * @memberof Dialog
	 */
	async close() {
		if (!this.opened) {
			return this.promise;
		}
		const opts = this.opts;
		// we give the opportunity to the outer world to block the process by canceling the event
		const beforeEvent = dispatch(this, conf.beforeCloseEvent, {
			bubbles: true,
			cancelable: true,
		});
		if (beforeEvent.defaultPrevented) {
			return this.promise;
		}
		this.opened = false;
		this.promise = this.promise
			.then(() => this.closing())
			.then(() => {
				dialogsStack.remove(this);
				this.removeEvents();
				dispatch(this, conf.closeEvent, {
					bubbles: true,
					cancelable: false,
				});
				// restore focus to the previous focused element
				if (this.previousActiveElement) {
					this.previousActiveElement.focus({preventScroll: true});
					this.previousActiveElement = null;
				} else {
					this.blur();
				}
				this.setAttribute('aria-hidden', 'true');
				// resume the elements that were suspended on opening
				if (opts.suspendElements) {
					this.resumeElementsOutside();
				}
			});
		return this.promise;
	}

	/**
	 * If it's opened, it close the dialog, if it's closed it opens the dialog
	 *
	 * @param {object} [params={}] @see {@link Dialog#open}
	 * @returns {Promise}
	 * @see {@link Dialog#open}
	 * @see {@link Dialog#close}
	 */
	toggle(params = {}) {
		if (this.opened) {
			return this.close();
		}
		return this.open(params);
	}

	/**
	 * @returns {boolean} the modal flag
	 */
	isModal() {
		return this.opts.modal;
	}

	/**
	 * @returns {boolean} the opened flag
	 */
	isOpened() {
		return this.opened;
	}

	/**
	 * @returns {boolean} true when the dialog is closed
	 */
	canBeOpened() {
		return !this.isOpened();
	}

	/**
	 * @returns {boolean} true when the dialog is opened
	 */
	canBeClosed() {
		return this.isOpened();
	}

	/**
	 * @returns {boolean} the coverable flag
	 */
	isCoverable() {
		return this.opts.coverable;
	}

	/**
	 * empty method that can be overriden in subclasses usable to clean something in the dialog
	 * after it closes
	 */
	afterClosing() {}

	/**
	 * Internal opening process
	 *
	 * @returns {Promise} resolves when the opening is complete
	 * @memberof Dialog
	 * @protected
	 * @ignore
	 *
	 */
	opening() {
		const opts = this.opts;
		return onFrame(() => {
			if (opts.modal) {
				// the modal node needs to be moved on the root, if it is not already there
				this.moveToRoot();

				// saving current state of scroll and root nodes. It will be restored when the modal closes
				this.previousScrollPos = {left: getScrollLeft(), top: getScrollTop()};
				this.freezeRootElementsPositions();
				if (opts.modal) {
					this.toggleRooElementsInert(true);
				}
				window.scrollTo({
					left: 0,
					top: 0,
					behavior: 'instant',
				});
			}
			return threeStateTransition(this).add(conf.openedClass);
		});
	}

	/**
	 * Internal closing process
	 *
	 * @returns {Promise} resolves when the closing is complete
	 * @memberof Dialog
	 * @protected
	 * @ignore
	 *
	 */
	closing() {
		const opts = this.opts;
		return onFrame(async () => {
			if (opts.modal) {
				classList(this).add(conf.fixedClass);
			}
			await threeStateTransition(this).remove(conf.openedClass);
			if (opts.modal) {
				this.restoreOriginalPosition();
				this.restoreRootElementsPositions();
				if (opts.modal) {
					this.toggleRooElementsInert(false);
				}
				await waitFrame();
				window.scrollTo({
					left: this.previousScrollPos.left,
					top: this.previousScrollPos.top,
					behavior: 'instant',
				});
				classList(this).remove(conf.fixedClass);
			}
			this.afterClosing();
		});
	}

	/**
	 * move the element to the body root
	 *
	 * @protected
	 * @ignore
	 */
	moveToRoot() {
		if (this.parentElement !== document.body) {
			this.originalParent = this.parentNode;
			this.originalNextSibling = this.nextElementSibling;
			document.body.appendChild(this);
		}
	}

	/**
	 * restores element's original position in the tree
	 *
	 * @protected
	 * @ignore
	 */
	restoreOriginalPosition() {
		if (this.originalParent) {
			if (this.originalNextSibling) {
				this.originalParent.insertBefore(this, this.originalNextSibling);
			} else {
				this.originalParent.appendChild(this);
			}
			this.originalParent = null;
		}
	}

	toggleRooElementsInert(value) {
		const rootElements = document.body.querySelectorAll(conf.rootElementsSelector);
		for (const element of rootElements) {
			if (element !== this) {
				this.toggleRooElementInert(element, value);
			}
		}
	}

	toggleRooElementInert(element, value) {
		element.inert = value;
	}

	/**
	 * Make elements fixed and positioned in order to keep their visual place in the viewport at the
	 * moment of the opening
	 *
	 * @protected
	 * @ignore
	 */
	freezeRootElementsPositions() {
		const rootElements = document.body.querySelectorAll(conf.rootElementsSelector);
		this.rootElements = [];
		for (const element of rootElements) {
			if (element !== this) {
				const cl = classList(element);
				this.rootElements.push({
					rect: element.getBoundingClientRect(),
					cl: cl,
					cssVars: cssVars(element),
					// they could be already covered by another dialog previously opened
					alreadyCovered: cl.contains(conf.coveredClass),
				});
			}
		}
		for (const entry of this.rootElements) {
			entry.cl.add(conf.coveredClass, this.coveredByClass);
			if (!entry.alreadyCovered) {
				entry.cssVars.set(conf.leftProp, entry.rect.left);
				entry.cssVars.set(conf.topProp, entry.rect.top);
			}
		}
	}

	/**
	 * restore elements' original position
	 *
	 * @protected
	 * @ignore
	 */
	restoreRootElementsPositions() {
		for (const entry of this.rootElements) {
			entry.cl.remove(this.coveredByClass);
			if (!entry.alreadyCovered) {
				entry.cl.remove(conf.coveredClass);
				entry.cssVars.remove(conf.leftProp);
				entry.cssVars.remove(conf.topProp);
			}
		}
		this.rootElements = [];
	}

	/**
	 * By default check if you are passing a content params and replace the dialog content with it,
	 * but it could be overriden in subclasses to do something different
	 *
	 * @param {object} [params={}] optional parameter used by the dialog
	 * @param {(string|DocumentFragment)} [params.content] when passed, will be used to replace the
	 * previous dialog content
	 * @protected
	 * @ignore
	 */
	processParams(params = {}) {
		if ('content' in params) {
			const contentElement = this.querySelector(dataset.selector(conf.contentAttr));
			if (contentElement) {
				const content = params.content;
				if (content instanceof DocumentFragment) {
					contentElement.innerHTML = '';
					contentElement.appendChild(content);
				} else {
					contentElement.innerHTML = content;
				}
			}
		}
	}

	/**
	 * Setup the events needed to keep the focus inside the element and to eventually close it
	 *
	 * @protected
	 * @ignore
	 */
	addEvents() {
		this.eventListeners = [
			// if we click on an anchor link that points outside the dialog we close it
			listen(this, 'click', this.onLinkClick.bind(this), {selector: 'a', capture: true}),
			// handle Esc
			listen(document, 'keydown', this.onKeyDownInside.bind(this), {capture: true}),
		];
		if (this.opts.modal && this.opts.closeOnClick) {
			// handles click outside the content to close the dialog
			this.eventListeners.push(
				listen(document, 'click', this.onClickForClose.bind(this), {capture: true})
			);
		}
	}

	/**
	 * cleaning
	 *
	 * @protected
	 * @ignore
	 */
	removeEvents() {
		for (const listener of this.eventListeners) {
			listener.unlisten();
		}
		this.eventListeners = [];
	}

	/**
	 * checks if the link point to an anchor targeting another part of the page outside the dialog,
	 * and close it in that case
	 *
	 * @param {Event} event the click event
	 * @param {HTMLAnchorElement} target the link clicked
	 * @protected
	 * @ignore
	 */
	onLinkClick(event, target) {
		if (dialogsStack.getLast() === this) {
			const href = target.getAttribute('href');
			if (href.charAt(0) !== '#' || !this.querySelector(href)) {
				event.preventDefault();
				this.close().then(() => {
					target.click();
				});
			}
		}
	}

	/**
	 * handles click events to see if the users want to close the Dialog by clicking outside the
	 * content
	 *
	 * @param {Event} event
	 * @protected
	 * @ignore
	 */
	onClickForClose(event) {
		// click inside the element but outside the content, usually on a pseudo-element used as a
		// backdrop
		if (
			this.opts.modal &&
			dialogsStack.getLast() === this &&
			(event.target === this || !this.contains(event.target))
		) {
			this.close();
		}
	}

	/**
	 * Handles keydown events checking for Esc or Tab keys
	 *
	 * @param {Event} event keydown event
	 * @protected
	 * @ignore
	 */
	onKeyDownInside(event) {
		// we handle events only if we are the dialog on top
		if (dialogsStack.getLast() === this) {
			if (this.opts.closeOnEsc && event.key === 'Escape') {
				event.preventDefault();
				this.close();
			}
		}
	}

	/**
	 * Suspends suspendible elements outside the Dialog
	 *
	 * @protected
	 * @ignore
	 */
	async suspendElementsOutside() {
		this.suspendedElements = [
			...(await lazyCustomElements.querySelectorAll(
				document,
				dataset.selector(conf.suspendibleAttr)
			)),
		].filter(
			(element) =>
				'suspend' in element && element.suspend instanceof Function && !this.contains(element)
		);
		for (const element of this.suspendedElements) {
			element.suspend();
		}
	}

	/**
	 * Resumes suspended elements
	 *
	 * @protected
	 * @ignore
	 */
	resumeElementsOutside() {
		for (const element of this.suspendedElements) {
			if ('resume' in element && element.resume instanceof Function) {
				element.resume();
			}
		}
		this.suspendedElements = [];
	}
}

export {Dialog};
